import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { useReduxState } from 'hooks';
import { useApiData } from 'lib/api';
import { LoadingIndicator } from 'components/common';
import { PageEmptyIndicator } from 'components/page';
import { EditorInner, useEditorContext, useEditorDispatch } from '.';

const Editor = () => {
  const router = useRouter();

  const {
    messages,
    pageState: { course }
  } = useReduxState();

  const {
    editorActiveLanguageVersion,
    editorChapters,
    editorCurrentChapter,
    editorIsSaving
  } = useEditorContext();

  const editorDispatch = useEditorDispatch();

  const [isMounted, setIsMounted] = useState(false);
  const [showLoading, setShowLoading] = useState(true);

  const chapterSlug = router.query.chapterSlug as string;
  const chapterPreviewVersion = router.query.chapterPreviewVersion as string;
  const coursePreviewVersion = router.query.coursePreviewVersion as string;

  // update chapters data in editor context
  const handleDataUpdated = (data) => {
    // get data from window.location to ensure updated values
    const chapterSlug = window.location.pathname.split('/')[5];
    const searchParams = new URL(window.location.href).searchParams;
    const coursePreviewVersion = searchParams.get('coursePreviewVersion');
    const chapterPreviewVersion = searchParams.get('chapterPreviewVersion');

    const currentChapter = data.chapters.find(
      (chapter) => chapter.slug === chapterSlug
    );

    const previewVersion = coursePreviewVersion || chapterPreviewVersion;

    const previewType = previewVersion
      ? coursePreviewVersion
        ? 'course'
        : 'chapter'
      : undefined;

    editorDispatch({
      type: 'updateContext',
      update: {
        editorChapters: data.chapters,
        editorCurrentChapter: currentChapter,
        editorPreviewType: previewType,
        editorPreviewVersion: previewVersion || undefined,
        editorPreviewVersionCourseLanguage: undefined,
        editorPreviewVersionCourseLanguageVersions: undefined
      }
    });

    setShowLoading(false);
  };

  // fetch chapters data
  const { loading, updateData } = useApiData({
    onDataUpdated: handleDataUpdated,
    url:
      `admin/courses/${course.id}` +
      (coursePreviewVersion
        ? `/versions/${coursePreviewVersion}/chapters-data`
        : '/chapters')
  });

  // set mounted
  useEffect(() => setIsMounted(true), []);

  // update current chapter if chapter slug has changed
  useEffect(() => {
    if (!chapterSlug || !editorChapters) return;

    const currentChapter = editorChapters.find(
      (chapter) => chapter.slug === chapterSlug
    );

    if (!currentChapter) {
      // try updating chapters data
      updateData();
      return;
    }

    editorDispatch({
      type: 'updateContext',
      update: { editorCurrentChapter: currentChapter }
    });
  }, [chapterSlug]);

  // update data if chapterPreviewVersion has changed
  useEffect(() => {
    if (isMounted) updateData();
  }, [chapterPreviewVersion]);

  // update chapters if editor has stopped saving
  useEffect(() => {
    if (isMounted && !editorIsSaving) updateData();
  }, [editorIsSaving]);

  // sync editorActiveLanguageVersion with languageVersion URL param
  useEffect(() => {
    const urlLanguageVersion =
      typeof router.query.languageVersion === 'string'
        ? router.query.languageVersion
        : null;
    if (
      urlLanguageVersion &&
      urlLanguageVersion !== editorActiveLanguageVersion
    ) {
      editorDispatch({
        type: 'updateContext',
        update: { editorActiveLanguageVersion: urlLanguageVersion }
      });
    }
  }, [router.query.languageVersion]);

  return loading && showLoading ? (
    <LoadingIndicator />
  ) : !editorCurrentChapter ? (
    <PageEmptyIndicator icon="book" title={messages.editChapterNotFound} />
  ) : (
    <EditorInner />
  );
};

export default Editor;
